import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { AppCommand } from './components/app-command.tsx'
import AppHeader from './components/app-header.tsx'
import { Confetti } from './components/confetti.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { Toaster } from './components/ui/sonner.tsx'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getConfetti } from './utils/confetti.server.ts'
import { getTenantPrisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { checkOrganizationSetup } from './utils/onboarding.server.ts'
import { getEffectiveUserContext } from './utils/session.server.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'


export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'ScholBooks | ' + data.tenantId + ' | The best fee management software for schools' : 'Error | ScholBooks' },
		{ name: 'description', content: `ScholBooks: The best fee management software for schools` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')

	// Get tenant ID for this request
	const { getTenantId } = await import('./utils/tenant.server')
	const tenantId = getTenantId(request)
	if (!tenantId) {
		throw new Error('No tenant identified')
	}

	// Get effective user context (handles role impersonation)
	const effectiveUserContext = await time(
		() => getEffectiveUserContext(request),
		{
			timings,
			type: 'getEffectiveUserContext',
			desc: 'get effective user context in root',
		},
	)

	let user = null
	let isAdmin = false

	if (effectiveUserContext) {
		// Get tenant-specific Prisma client
		const db = getTenantPrisma(tenantId)

		// Fetch user data with the effective context
		const userData = await time(
			() =>
				db.user.findUniqueOrThrow({
					select: {
						id: true,
						name: true,
						username: true,
						email: true,
						image: { select: { objectKey: true } },
					},
					where: { id: effectiveUserContext.userId },
				}),
			{ timings, type: 'find user', desc: 'find user in root' },
		)

		// Build user object with effective context
		user = {
			...userData,
			roles: effectiveUserContext.roles,
			// Add role-playing specific fields
			isImpersonating: effectiveUserContext.isImpersonating,
			impersonatedRoleName: effectiveUserContext.impersonatedRoleName,
			actualUsername: effectiveUserContext.actualUsername,
		}

		isAdmin = effectiveUserContext.isAdmin
	}

	const { toast, headers: toastHeaders } = await getToast(request)
	const { confettiId, headers: confettiHeaders } = getConfetti(request)
	const honeyProps = await honeypot.getInputProps()
	const isOnboardingComplete = await checkOrganizationSetup(tenantId)

	return data(
		{
			user,
			tenantId,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			confettiId,
			honeyProps,
			isAdmin,
			isOnboardingComplete,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				confettiHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	useToast(data.toast)
	const isOnboardingComplete = data.isOnboardingComplete

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="flex min-h-screen flex-col justify-between">
				<AppHeader isOnboardingComplete={isOnboardingComplete} />
				<div className="container mb-48 mt-12 flex flex-1 flex-col items-center gap-6">
					<Outlet />
				</div>
			</div>
			<Toaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
			<Confetti id={data.confettiId} />
			<AppCommand dialogMode={true} />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
